// Archivo: src/pages/ServicePage.js

import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import "../components/RichTextContent.css";
import {
  getPublicServiceById,
  getPublicServiceSections,
} from "../services/service.service";

// --- Componente Skeleton Loader (Mejorado para el nuevo layout) ---
const SkeletonLoader = () => (
  <div className="animate-pulse space-y-12">
    <div className="flex items-center gap-8 p-8 bg-gray-100 rounded-2xl">
      <div className="w-24 h-24 bg-gray-300 rounded-full flex-shrink-0"></div>
      <div className="flex-grow space-y-3">
        <div className="h-10 w-3/4 bg-gray-300 rounded-md"></div>
        <div className="h-6 w-full bg-gray-200 rounded-md"></div>
        <div className="h-6 w-5/6 bg-gray-200 rounded-md"></div>
      </div>
    </div>
    <div className="border-t border-gray-200 pt-12 space-y-4">
      <div className="h-16 bg-gray-200 rounded-lg"></div>
      <div className="h-16 bg-gray-200 rounded-lg"></div>
    </div>
  </div>
);

// --- Componente para los Enlaces de Sección (sin cambios) ---
const SectionLink = ({ to, index, title, description }) => (
  <Link
    to={to}
    className="group block p-6 bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-2xl hover:border-gob-azul-medio transform hover:-translate-y-1 transition-all duration-300"
  >
    <div className="flex items-start">
      <span className="mr-5 font-bold text-2xl text-gob-azul-medio group-hover:text-gob-azul-oscuro transition-colors duration-300">
        {index + 1}.
      </span>
      <div>
        <h3 className="font-semibold text-xl text-gray-800">{title}</h3>
        {description && (
          <div
            className="mt-1 text-base text-gray-500  mx-auto w-full "
            dangerouslySetInnerHTML={{
              __html:
                description.replace(/<[^>]*>/g, "").substring(0, 300) + "...",
            }}
          />
        )}
      </div>
      {/* <svg
        className="ml-auto h-6 w-6 text-gray-300 group-hover:text-gob-azul-medio transition-all duration-300 transform group-hover:translate-x-1"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 5l7 7-7-7"
        />
      </svg> */}
    </div>
  </Link>
);

const STORAGE_URL = process.env.REACT_APP_STORAGE_URL;

export default function ServicePage() {
  const { id: serviceId } = useParams();
  const [service, setService] = useState(null);
  const [sections, setSections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchAllData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [serviceData, sectionsData] = await Promise.all([
          getPublicServiceById(serviceId),
          getPublicServiceSections(serviceId),
        ]);
        setService(serviceData);
        setSections(sectionsData);
      } catch (err) {
        console.error("Error al conectar con la API:", err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAllData();
  }, [serviceId]);

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (error) {
    return (
      <div className="text-center p-10 bg-red-50 border border-red-200 rounded-lg">
        <h3 className="text-2xl font-bold text-red-700 mb-2">
          Ocurrió un error
        </h3>
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="animate-fade-in-up">
      {/* --- ENCABEZADO REDISEÑADO --- */}
      {/* 1. Se eliminó el contenedor blanco para un diseño más abierto */}
      <header className="flex flex-col md:flex-row items-center gap-8 py-8 mb-12 border-b border-gray-200">
        {service?.logo_path && (
          // 2. Se añadió un borde sutil al contenedor del logo
          <div className="flex-shrink-0 w-28 h-28 p-2 bg-white rounded-full shadow-md border border-gray-200">
            <img
              src={`${STORAGE_URL}/${service.logo_path}`}
              alt={`Logo de ${service.name}`}
              className="w-full h-full object-contain"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </div>
        )}
        <div className="text-center md:text-left w-full">
          <h1 className="text-5xl text-center md:text-left font-extrabold text-gob-azul-oscuro tracking-tight">
            {service?.name}
          </h1>
          <div
            className="mt-4 px-6 text-lg text-gray-500 mx-auto max-w-7xl text-left rich-text-content break-words"
            dangerouslySetInnerHTML={{
              __html: service?.description || "",
            }}
          />
        </div>
      </header>

      {/* Sumario de Secciones */}
      <main className="space-y-12">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-800 inline-block relative pb-2 after:content-[''] after:absolute after:left-1/2 after:-translate-x-1/2 after:bottom-0 after:w-20 after:h-1 after:bg-gob-azul-medio after:rounded-full">
            SUMARIO
          </h2>
        </div>

        <div className="space-y-6 ">
          {sections?.length > 0 ? (
            sections.map((section, index) => (
              <SectionLink
                key={section.id}
                to={`/servicios/${serviceId}/secciones/${section.id}`}
                index={index}
                title={section.title}
                description={section.description}
              />
            ))
          ) : (
            <div className="text-center text-gray-500 py-16 bg-gray-50 rounded-lg">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <h3 className="mt-2 text-lg font-medium text-gray-900">
                Sin Secciones
              </h3>
              <p className="mt-1 text-base text-gray-500">
                Actualmente no hay secciones disponibles para este servicio.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
